import {ValueObject} from '@s-ui/domain'

import {BreadcrumbSeoValueObject} from './BreadcrumbSeoValueObject.js'

export class ListBreadcrumbSeoValueObject extends ValueObject {
  #value

  constructor({value}) {
    super()
    this.#value = value
  }

  static create({value}) {
    return new ListBreadcrumbSeoValueObject({value: value.map(BreadcrumbSeoValueObject.create)})
  }

  static createFromAdsList({config, fields}) {
    const siteName = config.get('TENANT')
    const {ADS_LIST_BREADCRUMBS_MAP} = config.get('COMMON').BREADCRUMBS

    const breadcrumbs = new Set()

    ADS_LIST_BREADCRUMBS_MAP.forEach(field => {
      const currentField = fields[field]
      const hasBreadcrumb = Boolean(currentField?.text)

      if (hasBreadcrumb) {
        breadcrumbs.add(
          new BreadcrumbSeoValueObject({
            label: currentField.text,
            params: currentField.param,
            url: currentField.url
          })
        )
      }
    })

    breadcrumbs.add(
      new BreadcrumbSeoValueObject({label: 'COMMON_SEARCH_ENGINE', params: {siteName: siteName.toLowerCase()}})
    )

    return new ListBreadcrumbSeoValueObject({value: Array.from(breadcrumbs)})
  }

  static createFromRentingAdsList({config, fields, url = ''}) {
    const {ADS_LIST_BREADCRUMB_FIELDS} = config.get('COMMON').BREADCRUMBS

    const {SEMANTIC_KEYWORDS} = config.get('seo')

    const longestUrl = Object.values(fields).reduce((longest, current) => {
      return current.url && current.url.length > longest.length ? current.url : longest
    }, '')
    const path = longestUrl

    const RENTING_SEO_KEY_TO_FIELD_MAPPER = {
      [SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS]: ADS_LIST_BREADCRUMB_FIELDS.COMMITMENT_MONTHS,
      [SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS_NONE]: ADS_LIST_BREADCRUMB_FIELDS.COMMITMENT_MONTHS,
      [SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS_12]: ADS_LIST_BREADCRUMB_FIELDS.COMMITMENT_MONTHS,
      [SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS_24]: ADS_LIST_BREADCRUMB_FIELDS.COMMITMENT_MONTHS,
      [SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS_36]: ADS_LIST_BREADCRUMB_FIELDS.COMMITMENT_MONTHS,
      [SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS_48]: ADS_LIST_BREADCRUMB_FIELDS.COMMITMENT_MONTHS,
      [SEMANTIC_KEYWORDS.RENTING.VEHICLE_STATE_NEW]: ADS_LIST_BREADCRUMB_FIELDS.VEHICLE_STATE,
      [SEMANTIC_KEYWORDS.RENTING.VEHICLE_STATE_USED]: ADS_LIST_BREADCRUMB_FIELDS.VEHICLE_STATE,
      [SEMANTIC_KEYWORDS.RENTING.NO_ENTRY_FEE]: ADS_LIST_BREADCRUMB_FIELDS.ENTRY_FEE,
      [SEMANTIC_KEYWORDS.RENTING.TARGET_BUYER_PRIVATE]: ADS_LIST_BREADCRUMB_FIELDS.TARGET_BUYER,
      [SEMANTIC_KEYWORDS.RENTING.TARGET_BUYER_BUSINESS]: ADS_LIST_BREADCRUMB_FIELDS.TARGET_BUYER,
      [SEMANTIC_KEYWORDS.RENTING.CHEAP]: ADS_LIST_BREADCRUMB_FIELDS.SECTION_3,
      [SEMANTIC_KEYWORDS.RENTING.VEHICLE_TYPE_VAN]: ADS_LIST_BREADCRUMB_FIELDS.SECTION_3
    }

    const RENTING_BREADCRUMBS_FIELDS_MAPPER = {
      commitmentMonths: {
        [SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS]: {
          text: 'Renting por meses',
          url: `${path}${SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS}/`
        },
        [SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS_NONE]: {
          text: 'Renting sin permanencia',
          url: `${path}${SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS_NONE}/`
        },
        [SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS_12]: {
          text: 'Renting 12 meses',
          url: `${path}${SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS_12}/`
        },
        [SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS_24]: {
          text: 'Renting 24 meses',
          url: `${path}${SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS_24}/`
        },
        [SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS_36]: {
          text: 'Renting 36 meses',
          url: `${path}${SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS_36}/`
        },
        [SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS_48]: {
          text: 'Renting 48 meses',
          url: `${path}${SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS_48}/`
        }
      },
      vehicleState: {
        [SEMANTIC_KEYWORDS.RENTING.VEHICLE_STATE_NEW]: {
          text: 'Renting nuevo',
          url: `${path}${SEMANTIC_KEYWORDS.RENTING.VEHICLE_STATE_NEW}/`
        },
        [SEMANTIC_KEYWORDS.RENTING.VEHICLE_STATE_USED]: {
          text: 'Renting segunda mano',
          url: `${path}${SEMANTIC_KEYWORDS.RENTING.VEHICLE_STATE_USED}/`
        }
      },
      entryFee: {
        [SEMANTIC_KEYWORDS.RENTING.NO_ENTRY_FEE]: {
          text: 'Renting sin entrada',
          url: `${path}${SEMANTIC_KEYWORDS.RENTING.NO_ENTRY_FEE}/`
        },
        [SEMANTIC_KEYWORDS.RENTING.ENTRY_FEE]: {
          text: 'Con entrada',
          url: `${path}${SEMANTIC_KEYWORDS.RENTING.ENTRY_FEE}/`
        }
      },
      targetBuyer: {
        [SEMANTIC_KEYWORDS.RENTING.TARGET_BUYER_PRIVATE]: {
          text: 'Renting particulares',
          url: `${path}${SEMANTIC_KEYWORDS.RENTING.TARGET_BUYER_PRIVATE}/`
        },
        [SEMANTIC_KEYWORDS.RENTING.TARGET_BUYER_BUSINESS]: {
          text: 'Renting para empresas',
          url: `${path}${SEMANTIC_KEYWORDS.RENTING.TARGET_BUYER_BUSINESS}/`
        }
      },
      section3: {
        [SEMANTIC_KEYWORDS.RENTING.CHEAP]: {
          text: 'Renting baratos',
          url: `${path}${SEMANTIC_KEYWORDS.RENTING.CHEAP}/`
        },
        [SEMANTIC_KEYWORDS.RENTING.VEHICLE_TYPE_VAN]: {
          text: 'Renting furgonetas',
          url: `${path}${SEMANTIC_KEYWORDS.RENTING.VEHICLE_TYPE_VAN}/`
        }
      }
    }

    Object.values(SEMANTIC_KEYWORDS.RENTING)
      .filter(key => url.includes([key]))
      .forEach(seoKey => {
        const rentingField = RENTING_SEO_KEY_TO_FIELD_MAPPER[seoKey]
        fields[rentingField] = RENTING_BREADCRUMBS_FIELDS_MAPPER[rentingField][seoKey]
      })

    // Semantics está devolviendo información incorrecta para /renting/particulares/
    if (url.includes([SEMANTIC_KEYWORDS.RENTING.TARGET_BUYER_PRIVATE]) && fields.sellerType) {
      fields.sellerType = undefined
    }

    return ListBreadcrumbSeoValueObject.createFromAdsList({config, fields: fields || []})
  }

  get value() {
    return this.#value.map(breadcrumb => breadcrumb.value)
  }

  toJSON() {
    return {value: this.#value.map(breadcrumb => breadcrumb.toJSON())}
  }
}
